import { useCallback, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Slider from 'LuxKit/Slider/Slider'
import Caption from 'LuxKit/Typography/Caption'
import Clickable from 'components/Common/Clickable/Clickable'

const THUMB_SIZE = 12
const THUMB_RADIUS = THUMB_SIZE / 2

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  padding-block: ${rem(8)};
`

const SliderTrackContainer = styled.div`
  position: relative;
`

const EndpointDot = styled.div`
  position: absolute;
  top: 50%;
  width: ${rem(12)};
  height: ${rem(12)};
  border-radius: 50%;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.default.five};
  pointer-events: none;
  z-index: 1;

  &.start {
    left: ${rem(THUMB_RADIUS)};
    transform: translate(-50%, -50%);
  }

  &.end {
    right: ${rem(THUMB_RADIUS)};
    transform: translate(50%, -50%);
  }
`

/**
 * Overrides LuxKit Slider internals.
 * `> div` targets ProgressTrack, `> input` targets RangeInput.
 * These selectors are coupled to LuxKit/Slider's DOM structure.
 */
const StyledSlider = styled(Slider)`
  z-index: auto;

  > div {
    background: linear-gradient(
      to right,
      ${props => props.theme.palette.neutral.default.one} 0% var(--end, 0%),
      ${props => props.theme.palette.neutral.default.five} var(--end, 0%) 100%
    );
  }

  > input {
    z-index: 2;

    &::-webkit-slider-thumb {
      border-color: ${props => props.theme.palette.neutral.default.one};
      border-width: ${rem(2)};
    }

    &::-moz-range-thumb {
      border-color: ${props => props.theme.palette.neutral.default.one};
      border-width: ${rem(2)};
    }

    &::-ms-thumb {
      border-color: ${props => props.theme.palette.neutral.default.one};
      border-width: ${rem(2)};
    }
  }
`

const MarkerContainer = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;
`

const ClickableMarker = styled(Clickable)`
  pointer-events: auto;
  z-index: 3;
`

const MarkerLabel = styled(Caption)`
  position: absolute;
  bottom: calc(100% + ${rem(8)});
  white-space: nowrap;
`

const MarkerDot = styled.div`
  width: ${rem(12)};
  height: ${rem(12)};
  border-radius: 50%;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.default.five};
`

const Labels = styled.div`
  display: flex;
  justify-content: space-between;
`

interface ReferenceMarker {
  value: number
  label: string
}

interface Props {
  min: number
  max: number
  step?: number
  value?: number
  defaultValue?: number
  onChange?: (value: number) => void
  markers?: Array<ReferenceMarker>
  minLabel?: string
  maxLabel?: string
}

function AgentHubMarkerSlider({
  min,
  max,
  step = 1,
  value: controlledValue,
  defaultValue,
  onChange,
  markers: markersProp,
  minLabel,
  maxLabel,
}: Props) {
  const markers = useMemo(() => markersProp ?? [], [markersProp])

  const getMarkerLeft = useCallback((val: number) => {
    const percent = ((val - min) / (max - min)) * 100
    return `calc(${rem(THUMB_RADIUS)} + (100% - ${rem(THUMB_SIZE)}) * ${percent / 100})`
  }, [min, max])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.currentTarget.valueAsNumber)
  }, [onChange])

  return (
    <Wrapper>
      <SliderTrackContainer>
        <EndpointDot className="start" />
        <EndpointDot className="end" />
        {markers.map((marker) => (
          <MarkerContainer
            key={marker.label}
            style={{ left: getMarkerLeft(marker.value) }}
          >
            <ClickableMarker onClick={() => onChange?.(marker.value)}>
              <MarkerLabel variant="small" colour="neutral-two">
                {marker.label}
              </MarkerLabel>
              <MarkerDot />
            </ClickableMarker>
          </MarkerContainer>
        ))}
        <StyledSlider
          min={min}
          max={max}
          step={step}
          value={controlledValue}
          defaultValue={defaultValue}
          onChange={handleChange}
        />
      </SliderTrackContainer>
      {(minLabel || maxLabel) && (
        <Labels>
          {minLabel && <Caption variant="small" colour="neutral-two">{minLabel}</Caption>}
          {maxLabel && <Caption variant="small" colour="neutral-two">{maxLabel}</Caption>}
        </Labels>
      )}
    </Wrapper>
  )
}

export default AgentHubMarkerSlider
